import { toRaw } from 'vue';
import type { AxiosError } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import type {
  IGetAllPromotionsResponse,
  IPromotionBody,
  IPromotionDetailResponse,
  IPromotionUpdateBody,
} from '@/types/promotion.types';
import {
  code,
  promotion_id,
  promotion_page,
  statusProm,
  trigger,
  type,
} from '@/refs/promotion.refs';
import { restaurantId } from '@/refs/common.refs';
import { productId } from '@/refs/product.refs';

export const PromotionService = {
  /**
   * Crée une nouvelle promotion dans le système.
   *
   * Cette fonction envoie les données d'une promotion au backend pour
   * l'enregistrement dans la base de données.
   *
   * @async
   * @function createPromotion
   * @param {IPromotionBody} data - Données de la promotion à créer, incluant le nom, le type, le statut, la valeur, les dates et les éventuelles règles.
   * @returns {Promise<any>} Réponse de l'API contenant les informations de la promotion créée.
   * @throws {AxiosError} Lève une erreur si la requête HTTP échoue ou si le service renvoie une erreur.
   */
  createPromotion: async (data: IPromotionBody) => {
    try {
      const response = await axiosInstance.post(`promotions/create`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste paginée des promotions disponibles dans le système.
   *
   * Cette fonction construit les paramètres de filtrage et de pagination,
   * puis effectue une requête HTTP GET vers l'API pour obtenir
   * la liste des promotions enregistrées.
   *
   * Les paramètres pris en compte incluent :
   * - page : numéro de la page
   * - restau : identifiant du restaurant
   * - productId : filtre par produit
   * - code : filtre par code promotionnel
   * - trigger : filtre par type de déclencheur
   * - status : filtre par statut de la promotion
   * - type : filtre par type de promotion
   *
   * @async
   * @function getPromotion
   * @returns {Promise<IGetAllPromotionsResponse>} Promesse résolue contenant les données paginées des promotions retournées par l'API.
   * @throws {AxiosError} Lève une erreur si la requête HTTP échoue.
   */
  getPromotion: async () => {
    try {
      const page = Number(toRaw(promotion_page.value));
      const params = new URLSearchParams();
      params.append('pagination', 'true');
      params.append('page', page.toString());
      // if (restaurantId.value) params.append("restau", restaurantId.value);
      if (productId.value) params.append('productId', productId.value);
      if (code.value) params.append('code', code.value);
      if (trigger.value) params.append('trigger', trigger.value);
      if (statusProm.value) params.append('status', statusProm.value);
      if (type.value) params.append('type', type.value);

      const response = await axiosInstance.get<IGetAllPromotionsResponse>(
        `promotions/search?${params.toString()}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère une promotion spécifique à partir de son identifiant.
   *
   * Cette fonction vérifie d'abord si l'identifiant de la promotion (`promotion_id`)
   * est défini. Si ce n'est pas le cas, elle retourne `undefined`.
   * Sinon, elle envoie une requête HTTP GET à l'API pour récupérer
   * les détails complets de la promotion correspondante.
   *
   * @async
   * @function getPromotionById
   * @returns {Promise<IPromotionDetailResponse | undefined>}
   *          Promesse résolue contenant les données détaillées de la promotion,
   *          ou `undefined` si `promotion_id` est null.
   * @throws {AxiosError} Lève une erreur si la requête HTTP échoue.
   */
  getPromotionById: async () => {
    try {
      if (promotion_id.value === null) return;

      const promotionCode = toRaw(promotion_id.value.value);

      const response = await axiosInstance.get<IPromotionDetailResponse>(
        `promotions/get/${promotionCode}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère les données analytiques d'une promotion spécifique à partir de son identifiant.
   *
   * Cette fonction vérifie d'abord si l'identifiant de la promotion (`promotion_id`)
   * est défini. Si ce n'est pas le cas, elle retourne `undefined`.
   * Sinon, elle envoie une requête HTTP GET à l'API pour obtenir
   * les statistiques et analyses détaillées de la promotion correspondante.
   *
   * @async
   * @function getPromotionAnalyticsById
   * @returns {Promise<any | undefined>}
   *          Promesse résolue contenant les données analytiques de la promotion,
   *          ou `undefined` si `promotion_id` est null.
   * @throws {AxiosError} Lève une erreur si la requête HTTP échoue.
   */
  getPromotionAnalyticsById: async () => {
    try {
      if (promotion_id.value === null) return;

      const id = toRaw(promotion_id.value.value);

      const response = await axiosInstance.get<any>(
        `promotions/${id}/analytics`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour une promotion existante à partir de son identifiant.
   *
   * Cette fonction vérifie d'abord si l'identifiant de la promotion (`promotion_id`)
   * est défini. Si ce n'est pas le cas, elle retourne `undefined`.
   * Sinon, elle envoie une requête HTTP PUT à l'API avec les données fournies
   * pour mettre à jour les informations de la promotion correspondante.
   *
   * @async
   * @function updatePromotion
   * @param {Partial<IPromotionUpdateBody>} promotionData - Données partielles de la promotion à mettre à jour.
   * @returns {Promise<any | undefined>}
   *          Promesse résolue contenant la réponse de l'API après la mise à jour,
   *          ou `undefined` si `promotion_id` est null.
   * @throws {AxiosError} Lève une erreur si la requête HTTP échoue.
   */
  updatePromotion: async (promotionData: Partial<IPromotionUpdateBody>) => {
    try {
      if (promotion_id.value === null) return;

      const id = toRaw(promotion_id.value);

      const response = await axiosInstance.put<any>(
        `promotions/update/${id}`,
        promotionData,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime une promotion existante à partir de son identifiant.
   *
   * Cette fonction vérifie d'abord si l'identifiant de la promotion (`promotion_id`)
   * est défini. Si ce n'est pas le cas, elle retourne `undefined`.
   * Sinon, elle envoie une requête HTTP DELETE à l'API afin de supprimer
   * la promotion correspondante du système.
   *
   * @async
   * @function deletePromotion
   * @returns {Promise<any | undefined>}
   *          Promesse résolue contenant la réponse de l'API après suppression,
   *          ou `undefined` si `promotion_id` est null.
   * @throws {AxiosError} Lève une erreur si la requête HTTP échoue.
   */
  deletePromotion: async () => {
    try {
      if (promotion_id.value === null) return;

      const id = toRaw(promotion_id.value);

      const response = await axiosInstance.delete(`promotions/delete/${id}`, {
        headers: { 'Content-Type': 'application/json' },
      });

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
